/**
 * AgentVault "doctor" command — diagnostic checks and plist fixer.
 *
 * Detects stale macOS LaunchAgent plist paths (common after pnpm updates)
 * and offers to install a stable wrapper script so future updates don't
 * break the gateway.
 *
 * Usage: npx @agentvault/agentvault doctor [--fix]
 */

import { execSync } from "node:child_process";
import { existsSync, readFileSync, writeFileSync, chmodSync } from "node:fs";
import { createRequire } from "node:module";
import { join } from "node:path";
import { createInterface } from "node:readline";
import { openclawHome, readOpenClawConfig } from "./create-agent.js";
import { checkHarnessCompat, detectOpenClawVersion } from "./harness-compat.js";
import {
  checkHarnessDrift,
  describeHarnessCompat,
  describePluginVersion,
  describeSdkCapabilities,
  parseCliVersion,
} from "./preflight.js";
import {
  resolveSdkFn,
  HEARTBEAT_CANDIDATES,
  AGENT_EVENT_CANDIDATES,
  TRANSCRIPT_CANDIDATES,
} from "./openclaw-compat.js";

/** Optional SDK capabilities probed by doctor. Each degrades silently to a no-op. */
const SDK_CAPABILITIES = [
  { name: "heartbeat wake", candidates: HEARTBEAT_CANDIDATES },
  { name: "agent events", candidates: AGENT_EVENT_CANDIDATES },
  { name: "transcript updates", candidates: TRANSCRIPT_CANDIDATES },
];

/* ── Types ─────────────────────────────────────────────────────────────── */

export type CheckStatus = "pass" | "warn" | "fail" | "skip";

export interface CheckResult {
  name: string;
  status: CheckStatus;
  message: string;
}

export interface PlistInfo {
  plistPath: string;
  programArgs: string[];
  stalePaths: string[];
}

export interface PlistValidationResult {
  status: "ok" | "stale" | "missing" | "skip" | "already-fixed";
  plistPath?: string;
  stalePaths?: string[];
}

const PLIST_FILENAME = "ai.openclaw.gateway.plist";
const WRAPPER_SCRIPT_NAME = "start-gateway.sh";

/**
 * Our own version, resolved the same way cli.ts does it (dist/doctor.js ->
 * ../package.json is the package root). Returns null rather than throwing so a
 * packaging change degrades the check to "skip" instead of breaking doctor.
 */
export function resolvePluginVersion(): string | null {
  try {
    const req = createRequire(import.meta.url);
    const pkg = req("../package.json") as { version?: string };
    return typeof pkg?.version === "string" ? pkg.version : null;
  } catch {
    return null;
  }
}

/* ── Individual check functions ────────────────────────────────────────── */

export function checkOpenClawInstalled(): CheckResult {
  try {
    const out = execSync("openclaw --version", { stdio: "pipe", timeout: 10000 });
    const version = out.toString().trim();
    return { name: "OpenClaw installed", status: "pass", message: version };
  } catch {
    return {
      name: "OpenClaw installed",
      status: "fail",
      message: "'openclaw' not found in PATH",
    };
  }
}

export function checkConfigExists(home: string): CheckResult {
  const configPath = join(home, "openclaw.json");
  if (!existsSync(configPath)) {
    return {
      name: "Config exists",
      status: "fail",
      message: `Not found: ${configPath}`,
    };
  }
  try {
    JSON.parse(readFileSync(configPath, "utf-8"));
    return { name: "Config exists", status: "pass", message: configPath };
  } catch {
    return {
      name: "Config exists",
      status: "fail",
      message: `Invalid JSON: ${configPath}`,
    };
  }
}

export function checkAgentVaultChannel(config: any): CheckResult {
  if (!config) {
    return {
      name: "AgentVault channel",
      status: "fail",
      message: "Config is null",
    };
  }
  if (config.channels?.agentvault) {
    return { name: "AgentVault channel", status: "pass", message: "channels.agentvault present" };
  }
  return {
    name: "AgentVault channel",
    status: "fail",
    message: "channels.agentvault not found in config",
  };
}

export function checkPluginsAllow(config: any): CheckResult {
  if (!config) {
    return { name: "Plugin allow-listed", status: "warn", message: "Config is null" };
  }
  const allow = config.plugins?.allow;
  if (!Array.isArray(allow)) {
    return {
      name: "Plugin allow-listed",
      status: "warn",
      message: "plugins.allow not defined",
    };
  }
  if (allow.includes("agentvault")) {
    return { name: "Plugin allow-listed", status: "pass", message: "agentvault in plugins.allow" };
  }
  return {
    name: "Plugin allow-listed",
    status: "warn",
    message: "'agentvault' not in plugins.allow list",
  };
}

export function checkPlist(): CheckResult {
  if (process.platform !== "darwin") {
    return { name: "macOS LaunchAgent plist", status: "skip", message: "Not macOS" };
  }
  const result = validatePlist();
  switch (result.status) {
    case "ok":
      return { name: "macOS LaunchAgent plist", status: "pass", message: "Plist paths valid" };
    case "already-fixed":
      return { name: "macOS LaunchAgent plist", status: "pass", message: "Wrapper script already configured" };
    case "stale":
      return {
        name: "macOS LaunchAgent plist",
        status: "fail",
        message: `Stale path: ${result.stalePaths?.[0] ?? "unknown"}`,
      };
    case "missing":
      return {
        name: "macOS LaunchAgent plist",
        status: "warn",
        message: `Plist not found: ${result.plistPath ?? PLIST_FILENAME}`,
      };
    default:
      return { name: "macOS LaunchAgent plist", status: "skip", message: "Skipped" };
  }
}

export function checkGatewayPort(): CheckResult {
  try {
    execSync("curl -sf --max-time 3 http://127.0.0.1:18789/", { stdio: "pipe" });
    return { name: "Gateway port (18789)", status: "pass", message: "Responding" };
  } catch {
    return {
      name: "Gateway port (18789)",
      status: "fail",
      message: "No response on 127.0.0.1:18789",
    };
  }
}

export function checkAgentDataDirs(config: any): CheckResult {
  if (!config) {
    return { name: "Agent data dirs", status: "warn", message: "Config is null" };
  }

  const home = process.env.HOME ?? "";
  const warnings: string[] = [];

  // Single-account mode
  const av = config.channels?.agentvault;
  if (!av) {
    return { name: "Agent data dirs", status: "warn", message: "No agentvault channel" };
  }

  const checkDir = (label: string, dataDir: string) => {
    const resolved = dataDir.replace(/^~/, home);
    if (!existsSync(resolved)) {
      warnings.push(`${label}: dir missing (${resolved})`);
      return;
    }
    const statePath = join(resolved, "agentvault.json");
    if (!existsSync(statePath)) {
      warnings.push(`${label}: no agentvault.json`);
    }
  };

  if (av.accounts && typeof av.accounts === "object") {
    for (const [id, acct] of Object.entries(av.accounts) as [string, any][]) {
      if (acct.dataDir) checkDir(id, acct.dataDir);
    }
  } else if (av.dataDir) {
    checkDir("default", av.dataDir);
  }

  if (warnings.length > 0) {
    return { name: "Agent data dirs", status: "warn", message: warnings.join("; ") };
  }
  return { name: "Agent data dirs", status: "pass", message: "All data dirs valid" };
}

export function checkPm2Status(): CheckResult {
  try {
    execSync("pm2 --version", { stdio: "pipe", timeout: 5000 });
  } catch {
    return { name: "pm2 status", status: "skip", message: "pm2 not installed" };
  }

  try {
    const info = execSync("pm2 describe openclaw-gateway", { stdio: "pipe", timeout: 5000 });
    const out = info.toString();
    if (out.includes("online")) {
      return { name: "pm2 status", status: "pass", message: "openclaw-gateway: online" };
    }
    if (out.includes("stopped")) {
      return { name: "pm2 status", status: "warn", message: "openclaw-gateway: stopped" };
    }
    return { name: "pm2 status", status: "warn", message: "openclaw-gateway: unknown state" };
  } catch {
    return { name: "pm2 status", status: "warn", message: "openclaw-gateway not registered in pm2" };
  }
}

/* ── Plist parsing / validation ────────────────────────────────────────── */

export function plistPath(): string {
  const home = process.env.HOME ?? "";
  return join(home, "Library", "LaunchAgents", PLIST_FILENAME);
}

export function parsePlist(plistFile: string): PlistInfo | null {
  if (!existsSync(plistFile)) return null;

  try {
    const json = execSync(`plutil -convert json -o - "${plistFile}"`, {
      stdio: "pipe",
      timeout: 5000,
    }).toString();

    const parsed = JSON.parse(json);
    const programArgs: string[] = parsed.ProgramArguments ?? [];
    const stalePaths: string[] = [];
    const wrapperPath = join(process.env.HOME ?? "", ".openclaw", WRAPPER_SCRIPT_NAME);

    for (const arg of programArgs) {
      // Check absolute paths (skip flags and relative args)
      if (arg.startsWith("/") && arg !== "/bin/bash" && arg !== wrapperPath) {
        if (!existsSync(arg)) {
          stalePaths.push(arg);
        }
      }
    }

    return { plistPath: plistFile, programArgs, stalePaths };
  } catch {
    return null;
  }
}

export function validatePlist(): PlistValidationResult {
  if (process.platform !== "darwin") {
    return { status: "skip" };
  }

  const path = plistPath();
  if (!existsSync(path)) {
    return { status: "missing", plistPath: path };
  }

  const info = parsePlist(path);
  if (!info) {
    return { status: "missing", plistPath: path };
  }

  // Check if already using our wrapper script
  const home = process.env.HOME ?? "";
  const wrapperPath = join(home, ".openclaw", WRAPPER_SCRIPT_NAME);
  if (info.programArgs.includes(wrapperPath)) {
    return { status: "already-fixed", plistPath: path };
  }

  if (info.stalePaths.length > 0) {
    return { status: "stale", plistPath: path, stalePaths: info.stalePaths };
  }

  return { status: "ok", plistPath: path };
}

/* ── Plist fix ─────────────────────────────────────────────────────────── */

export function applyPlistFix(info: PlistInfo): boolean {
  const home = process.env.HOME ?? "";
  const openclawDir = join(home, ".openclaw");
  const wrapperPath = join(openclawDir, WRAPPER_SCRIPT_NAME);

  // 1. Create wrapper script (PATH needed for launchd's minimal environment)
  const wrapperContent = `#!/bin/bash
export PATH="$HOME/.local/bin:$HOME/.pnpm/bin:$HOME/Library/pnpm/bin:$HOME/Library/pnpm:/opt/homebrew/bin:/usr/local/bin:$PATH"
exec "$(which openclaw)" gateway start "$@"
`;

  try {
    writeFileSync(wrapperPath, wrapperContent, "utf-8");
    chmodSync(wrapperPath, 0o755);
    console.log(`  Created wrapper: ${wrapperPath}`);
  } catch (err) {
    console.error(`  Failed to create wrapper script: ${(err as Error).message}`);
    return false;
  }

  // 2. Update plist via PlistBuddy
  const plistBuddy = "/usr/libexec/PlistBuddy";
  const plist = info.plistPath;

  try {
    // Set ProgramArguments:0 = /bin/bash
    execSync(`${plistBuddy} -c "Set :ProgramArguments:0 /bin/bash" "${plist}"`, { stdio: "pipe" });

    // Set ProgramArguments:1 = wrapper path
    try {
      execSync(`${plistBuddy} -c "Set :ProgramArguments:1 ${wrapperPath}" "${plist}"`, { stdio: "pipe" });
    } catch {
      // Index 1 might not exist — add it
      execSync(`${plistBuddy} -c "Add :ProgramArguments:1 string ${wrapperPath}" "${plist}"`, { stdio: "pipe" });
    }

    // Delete extra args (index 2+) — loop in reverse to avoid index shifts
    // First, count how many args there are
    let argCount = info.programArgs.length;
    for (let i = argCount - 1; i >= 2; i--) {
      try {
        execSync(`${plistBuddy} -c "Delete :ProgramArguments:${i}" "${plist}"`, { stdio: "pipe" });
      } catch {
        // Already gone
      }
    }

    console.log("  Updated plist ProgramArguments.");
  } catch (err) {
    console.error(`  Failed to update plist: ${(err as Error).message}`);
    return false;
  }

  // 3. Reload launchd
  try {
    execSync(`launchctl unload "${plist}"`, { stdio: "pipe" });
  } catch {
    // May not be loaded — that's fine
  }

  try {
    execSync(`launchctl load "${plist}"`, { stdio: "pipe" });
    console.log("  Reloaded LaunchAgent.");
  } catch (err) {
    console.error(`  Failed to reload LaunchAgent: ${(err as Error).message}`);
    console.log(`  Try manually: launchctl load "${plist}"`);
    return false;
  }

  return true;
}

/* ── Main doctor command ───────────────────────────────────────────────── */

export async function runDoctorCommand(options?: { fix?: boolean }): Promise<void> {
  const home = openclawHome();

  console.log(`
╔══════════════════════════════════════════════╗
║       AgentVault — Doctor Diagnostics        ║
╚══════════════════════════════════════════════╝
`);

  const results: CheckResult[] = [];

  // 1. OpenClaw installed
  const openclawInstalled = checkOpenClawInstalled();
  results.push(openclawInstalled);

  // 1a-1c. #480 Lever 3 — the preflight questions. These run BEFORE the config
  // checks on purpose: if the harness is out of range or the operator is driving
  // a different gateway than the plugin loaded, every config finding below is
  // suspect and should be read in that light.
  //
  // Reuse the output already obtained above rather than shelling out a second
  // time. It needs parsing, not just reading: `openclaw --version` prints
  // "OpenClaw 2026.7.1 (2d2ddc4)", and comparing that raw would report drift on
  // a matched install.
  const cliVersion =
    openclawInstalled.status === "pass" ? parseCliVersion(openclawInstalled.message) : null;
  const moduleVersion = detectOpenClawVersion();

  results.push(describePluginVersion(resolvePluginVersion()));
  results.push(describeHarnessCompat(checkHarnessCompat(moduleVersion)));
  results.push(checkHarnessDrift(cliVersion, moduleVersion));

  // 1d. Are the optional SDK capabilities actually reachable on this harness?
  // Each one degrades to a no-op when its symbol moves, so this line is the
  // only place a customer can see that heartbeat-wake / agent-events /
  // transcript-updates have gone dead. All three were dead on 2026.7.1.
  results.push(
    describeSdkCapabilities(
      await Promise.all(
        SDK_CAPABILITIES.map(async ({ name, candidates }) => ({
          name,
          resolved: (await resolveSdkFn(candidates)) !== false,
        })),
      ),
    ),
  );

  // 2. Config exists
  results.push(checkConfigExists(home));

  // 3-4. Parse config for channel/plugin checks
  let config: any = null;
  try {
    config = readOpenClawConfig(home);
  } catch {
    // Config unreadable — already captured by check #2
  }

  results.push(checkAgentVaultChannel(config));
  results.push(checkPluginsAllow(config));

  // 5. Plist (macOS only)
  results.push(checkPlist());

  // 6. Gateway port
  results.push(checkGatewayPort());

  // 7. Agent data dirs
  results.push(checkAgentDataDirs(config));

  // 8. pm2 status
  results.push(checkPm2Status());

  // Print results
  const statusLabels: Record<CheckStatus, string> = {
    pass: "pass",
    warn: "WARN",
    fail: "FAIL",
    skip: "skip",
  };

  for (const r of results) {
    const label = `[${statusLabels[r.status]}]`;
    console.log(`  ${label.padEnd(6)} ${r.name} — ${r.message}`);
  }

  const passed = results.filter((r) => r.status === "pass").length;
  const warnings = results.filter((r) => r.status === "warn").length;
  const failed = results.filter((r) => r.status === "fail").length;

  console.log(`
  ─────────────────────────────────────────────
  ${passed} passed  ${warnings} warnings  ${failed} failed
  ─────────────────────────────────────────────
`);

  if (failed > 0) {
    process.exitCode = 1;
  }

  // Offer plist fix if stale
  const plistResult = validatePlist();
  if (plistResult.status === "stale") {
    const plistInfo = parsePlist(plistResult.plistPath!);
    if (!plistInfo) return;

    if (options?.fix) {
      console.log("  Applying plist fix (--fix)...\n");
      const ok = applyPlistFix(plistInfo);
      if (ok) {
        console.log("\n  ✅ Plist fixed. Gateway should start cleanly now.\n");
      } else {
        console.log("\n  ❌ Plist fix failed. See errors above.\n");
      }
    } else {
      // Interactive prompt
      const answer = await ask("  Stale plist detected. Apply wrapper script fix? [y/N] ");
      if (answer.trim().toLowerCase() === "y") {
        console.log("");
        const ok = applyPlistFix(plistInfo);
        if (ok) {
          console.log("\n  ✅ Plist fixed. Gateway should start cleanly now.\n");
        } else {
          console.log("\n  ❌ Plist fix failed. See errors above.\n");
        }
      } else {
        console.log(`
  To fix later, run:
    npx @agentvault/agentvault doctor --fix
`);
      }
    }
  }
}

/* ── Helpers ───────────────────────────────────────────────────────────── */

function ask(question: string): Promise<string> {
  return new Promise((resolve) => {
    const rl = createInterface({
      input: process.stdin,
      output: process.stdout,
      terminal: true,
    });
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer);
    });
  });
}
